// 05 · Historial: chips LIVE / 📡 RADAR, resumen .sum, Lista / Calendario
// mensual (intensidad por aciertos netos del día; tocar día filtra),
// filas .hrow. Sin unidades ni stake (decisión 1): el resumen muestra
// acierto, señales y W-L. RADAR: resumen claro con acierto retrospectivo y
// edge medio, sin unidades. Fuentes: signals_history (useClientStats,
// mismos helpers que Performance Lab) y /radar_vip_results.
// 15.3 vacío (cuenta nueva).
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import useClientStats from '../../hooks/useClientStats';
import useRadarResults from '../../../hooks/useRadarResults';
import { santiagoDateStr, statsOfSubset } from '../../../components/PerformanceLab';
import { pickOf, matchOf } from '../../components/SignalCard';
import { NoAccess } from '../cards';
import { EmptyFull, OffBar, SigSkeleton, I } from '../ui';
import { useOnline, ventanaDe } from '../hooks';
import { fmtNum, gidDeSenal, shortName } from '../live';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const esHbSig = (s) => s.sport === 'handball' || s.match_type === 'handball' || /handball|hbl|ehf|asobal|starligue|handbold|handboll/i.test(String(s.league || s.liga || ''));
const shortMatch = (m) => m.split(' vs ').map(shortName).join(' vs ');
const fmtDia = (d) => { const [, m, dd] = d.split('-'); return `${parseInt(dd, 10)} de ${MESES[parseInt(m, 10) - 1]}`; };
const ymOf = (d) => d.slice(0, 7);
function mesLabel(ym) { const [y, m] = ym.split('-'); return `${cap(MESES[parseInt(m, 10) - 1])} ${y}`; }
function shiftYm(ym, n) { const [y, m] = ym.split('-').map(Number); const d = new Date(Date.UTC(y, m - 1 + n, 1)); return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`; }

export default function Historial() {
  const navigate = useNavigate();
  const { hasActiveAccess, isAdmin } = useAuth();
  const acceso = hasActiveAccess || isAdmin;
  const { signals, loading } = useClientStats();
  const { alerts, loading: lRadar } = useRadarResults();
  const { online, sinceStr } = useOnline();
  const hoy = santiagoDateStr(new Date());
  const [canal, setCanal] = useState('live'); // live | radar
  const [vista, setVista] = useState('lista'); // lista | cal
  const [periodo, setPeriodo] = useState(0); // 0,1,2 = meses; 3 = todo
  const [calYm, setCalYm] = useState(ymOf(hoy));
  const [diaSel, setDiaSel] = useState(null);

  const meses = [0, 1, 2].map((n) => shiftYm(ymOf(hoy), -n));
  const sigDia = useMemo(() => signals.map((s) => ({ ...s, _dia: santiagoDateStr(new Date(s.started_at || 0)) })), [signals]);

  // ── LIVE ──
  const subset = useMemo(() => (periodo === 3 ? sigDia : sigDia.filter((s) => ymOf(s._dia) === meses[periodo])), [sigDia, periodo, meses]);
  const st = statsOfSubset(subset);
  const enJuego = subset.filter((s) => !['WIN', 'LOSS', 'PUSH'].includes(s.result)).length;
  const grupos = useMemo(() => {
    const map = new Map();
    for (const s of subset) { if (!map.has(s._dia)) map.set(s._dia, []); map.get(s._dia).push(s); }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [subset]);

  // calendario del mes calYm: neto de aciertos por día
  const calDias = useMemo(() => {
    const m = new Map();
    for (const s of sigDia) {
      if (ymOf(s._dia) !== calYm) continue;
      const e = m.get(s._dia) || { w: 0, l: 0, n: 0, hb: 0, bb: 0 };
      e.n++; if (esHbSig(s)) e.hb++; else e.bb++;
      if (s.result === 'WIN') e.w++; else if (s.result === 'LOSS') e.l++;
      m.set(s._dia, e);
    }
    return m;
  }, [sigDia, calYm]);
  const mejorDia = useMemo(() => { let best = null; for (const [d, e] of calDias) { if (!best || e.w - e.l > best.net) best = { d, net: e.w - e.l }; } return best ? best.d : null; }, [calDias]);
  const diaActivo = diaSel && ymOf(diaSel) === calYm ? diaSel : mejorDia;
  const [cy, cm] = calYm.split('-').map(Number);
  const primero = new Date(Date.UTC(cy, cm - 1, 1)); const nDias = new Date(Date.UTC(cy, cm, 0)).getUTCDate();
  const offset = (primero.getUTCDay() + 6) % 7; // lunes primero
  const celdas = [...Array(offset).fill(null), ...Array.from({ length: nDias }, (_, i) => `${calYm}-${String(i + 1).padStart(2, '0')}`)];

  // ── RADAR ──
  const radarSubset = useMemo(() => (periodo === 3 ? alerts : alerts.filter((a) => ymOf(a.fecha) === meses[periodo])), [alerts, periodo, meses]);
  const rRes = radarSubset.filter((a) => a.resolved);
  const rW = rRes.filter((a) => a.won).length;
  const rEdge = rRes.length ? rRes.reduce((acc, a) => acc + (a.edge || 0), 0) / rRes.length : null;
  const rGrupos = useMemo(() => {
    const map = new Map();
    for (const a of radarSubset) { if (!map.has(a.fecha)) map.set(a.fecha, []); map.get(a.fecha).push(a); }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [radarSubset]);

  const label = periodo === 3 ? 'Todo' : mesLabel(meses[periodo]);
  const vacioTotal = !loading && signals.length === 0 && canal === 'live';

  const Row = ({ s }) => {
    const win = s.result === 'WIN', loss = s.result === 'LOSS', pend = !win && !loss && s.result !== 'PUSH';
    return (
      <button type="button" className="hrow" onClick={() => navigate(`/app/partido/${gidDeSenal(s)}`, { state: { signal: s } })}>
        <div className={`res ${win ? 'w' : loss ? 'l' : 'p'}`}>{win ? 'W' : loss ? 'L' : '·'}</div>
        <div className="b"><p className="m">{shortMatch(matchOf(s))}</p>
          <p className="s">{esHbSig(s) ? '🤾' : '🏀'} {pickOf(s)} · {s.league || s.liga || '—'}{pend ? <> · <b style={{ color: 'var(--neg)' }}>en juego</b></> : s.result === 'PUSH' ? ' · push' : ''}</p></div>
        <div className="u"><p className={`p ${win ? 'g' : loss ? 'n' : 'm'}`}>Edge {fmtNum(s.edge_initial ?? s.edge)}</p><p className="d">{ventanaDe(s)}</p></div>
      </button>
    );
  };

  return (
    <div className="v3"><div className="scroll safe-top" style={vacioTotal ? { display: 'flex', flexDirection: 'column' } : undefined}>
      <div className="pgh"><h1>Historial</h1><p>{canal === 'radar' ? 'Señales RADAR: análisis retrospectivo del motor, sin stake.' : 'Cada señal cerrada, con su resultado y su rendimiento.'}</p></div>
      {!online && <OffBar since={sinceStr} />}
      <div className="chips">
        <button type="button" className={`v3chip${canal === 'live' ? ' on' : ''}`} onClick={() => setCanal('live')}>LIVE</button>
        <button type="button" className={`v3chip${canal === 'radar' ? ' on' : ''}`} onClick={() => setCanal('radar')}>📡 RADAR</button>
      </div>

      {vacioTotal ? (
        <EmptyFull icon={<I.cal width={26} height={26} strokeWidth="2" />} title="Tu historial empieza con la primera señal" cta="Ver rendimiento del motor" onCta={() => navigate('/historial')}>
          Aquí verás cada señal cerrada, su resultado y el acierto del período. Mientras tanto, puedes revisar cómo le fue al motor.
        </EmptyFull>
      ) : canal === 'live' ? (
        <>
          <div className="sum"><div className="in">
            <p className="mo">{label}</p>
            <p className="big">{st.winRate != null ? `${st.winRate.toFixed(0)}%` : '—'}<em>de acierto del período</em></p>
            <div className="r"><div><p className="l">Señales</p><p className="v">{st.total}</p></div><div><p className="l">W-L</p><p className="v o">{st.wins}-{st.losses}</p></div><div><p className="l">En juego</p><p className="v g">{enJuego}</p></div></div>
          </div></div>
          <div className="chips" style={{ paddingTop: 16 }}>
            <button type="button" className={`v3chip${vista === 'lista' ? ' on' : ''}`} onClick={() => setVista('lista')}>Lista</button>
            <button type="button" className={`v3chip${vista === 'cal' ? ' on' : ''}`} onClick={() => setVista('cal')}>Calendario</button>
            {vista === 'lista' && meses.map((ym, i) => <button key={ym} type="button" className={`v3chip${periodo === i ? ' on' : ''}`} onClick={() => setPeriodo(i)}>{cap(MESES[parseInt(ym.slice(5), 10) - 1])}</button>)}
            {vista === 'lista' && <button type="button" className={`v3chip${periodo === 3 ? ' on' : ''}`} onClick={() => setPeriodo(3)}>Todo</button>}
          </div>

          {vista === 'lista' && (loading ? <><SigSkeleton /><SigSkeleton /></> : grupos.length === 0
            ? <div className="empty"><h3>Sin señales en este período</h3><p>Elige otro mes o revisa "Todo".</p></div>
            : (acceso ? grupos : grupos.slice(0, 3)).map(([dia, items]) => (
              <div key={dia}><p className="grp">{fmtDia(dia)}</p>{items.map((s) => <Row key={s.id} s={s} />)}</div>
            )))}
          {vista === 'lista' && !acceso && grupos.length > 3 && <NoAccess que="Solo ves los últimos 3 días del historial." />}

          {vista === 'cal' && (
            <>
              <div className="month-nav">
                <button type="button" className="arw" aria-label="Mes anterior" onClick={() => { setCalYm(shiftYm(calYm, -1)); setDiaSel(null); }}><I.back width={14} height={14} stroke="#6E6E73" strokeWidth="2.6" /></button>
                <b>{mesLabel(calYm)}</b>
                <button type="button" className="arw" aria-label="Mes siguiente" disabled={calYm >= ymOf(hoy)} onClick={() => { setCalYm(shiftYm(calYm, 1)); setDiaSel(null); }}><I.chev width={14} height={14} stroke="#6E6E73" strokeWidth="2.6" /></button>
              </div>
              <div className="cal">
                {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <span key={`dn${i}`} className="dn">{d}</span>)}
                {celdas.map((d, i) => {
                  if (!d) return <span key={`e${i}`} />;
                  const e = calDias.get(d); const net = e ? e.w - e.l : 0;
                  const cls = [e && net >= 3 ? 'gg' : e && net > 0 ? 'g' : e && net < 0 ? 'r' : '', d === hoy ? 't' : '', d === diaActivo ? 'sel' : ''].filter(Boolean).join(' ');
                  return <span key={d} className={cls} role="button" tabIndex={0} onClick={() => setDiaSel(d)} onKeyDown={(ev) => ev.key === 'Enter' && setDiaSel(d)}>{parseInt(d.slice(8), 10)}</span>;
                })}
              </div>
              <div className="v3leg" style={{ padding: '0 4px' }}>
                <span><i className="sq" style={{ background: 'var(--pos-soft)' }} />Día positivo</span>
                <span><i className="sq" style={{ background: 'var(--pos)' }} />+3 aciertos netos</span>
                <span><i className="sq" style={{ background: 'var(--neg-soft)' }} />Negativo</span>
              </div>
              {diaActivo && calDias.get(diaActivo) && (() => {
                const e = calDias.get(diaActivo); const dow = DIAS[new Date(`${diaActivo}T12:00:00Z`).getUTCDay()];
                const pct = e.w + e.l > 0 ? Math.round((e.w / (e.w + e.l)) * 100) : null;
                return (
                  <>
                    <p className="grp">{cap(dow)} {parseInt(diaActivo.slice(8), 10)}{diaActivo === mejorDia ? ' · mejor día' : ''}</p>
                    <div className="hrow">
                      <div className={`res ${e.w - e.l > 0 ? 'w' : e.w - e.l < 0 ? 'l' : 'p'}`}>{e.w - e.l > 0 ? 'W' : e.w - e.l < 0 ? 'L' : '·'}</div>
                      <div className="b"><p className="m">{e.w} de {e.w + e.l} señales</p><p className="s">Básquet {e.bb} · Handball {e.hb}</p></div>
                      <div className="u"><p className={`p ${e.w - e.l > 0 ? 'g' : e.w - e.l < 0 ? 'n' : 'm'}`}>{e.w}-{e.l}</p><p className="d">{pct != null ? `${pct}% acierto` : 'sin cerrar'}</p></div>
                    </div>
                    {sigDia.filter((s) => s._dia === diaActivo).map((s) => <Row key={s.id} s={s} />)}
                  </>
                );
              })()}
              {calDias.size === 0 && <div className="empty" style={{ marginTop: 12 }}><h3>Sin señales este mes</h3><p>Navega a otro mes con las flechas.</p></div>}
            </>
          )}
        </>
      ) : (
        <>
          <div className="sum light"><div className="in">
            <p className="mo">{label} · Radar</p>
            <p className="big">{rRes.length ? `${Math.round((rW / rRes.length) * 100)}%` : '—'}<em>de acierto retrospectivo</em></p>
            <div className="r"><div><p className="l">Revisadas</p><p className="v">{rRes.length}</p></div><div><p className="l">Habrían entrado</p><p className="v g">{rW}</p></div><div><p className="l">Edge medio</p><p className="v o">{rEdge == null ? '—' : fmtNum(rEdge)}</p></div></div>
          </div></div>
          <div className="chips" style={{ paddingTop: 16 }}>
            {meses.map((ym, i) => <button key={ym} type="button" className={`v3chip${periodo === i ? ' on' : ''}`} onClick={() => setPeriodo(i)}>{cap(MESES[parseInt(ym.slice(5), 10) - 1])}</button>)}
            <button type="button" className={`v3chip${periodo === 3 ? ' on' : ''}`} onClick={() => setPeriodo(3)}>Todo</button>
          </div>
          {lRadar ? <SigSkeleton /> : rGrupos.length === 0
            ? <div className="empty"><h3>Sin alertas RADAR en este período</h3><p>Elige otro mes o revisa "Todo".</p></div>
            : (acceso ? rGrupos : rGrupos.slice(0, 3)).map(([dia, items]) => (
              <div key={dia}><p className="grp">{fmtDia(dia)}</p>
                {items.map((a) => (
                  <button type="button" key={a.id} className="hrow" onClick={() => navigate(`/app/partido/${a.gid}`)}>
                    <div className={`res ${!a.resolved ? 'p' : a.won ? 'w' : 'l'}`}>{!a.resolved ? '·' : a.won ? 'W' : 'L'}</div>
                    <div className="b"><p className="m">{shortMatch(a.partido.replace(' @ ', ' vs '))}</p><p className="s">🏀 {a.side} {a.linea ?? '—'} · {a.ventana} · {a.liga}{!a.resolved ? <> · <b style={{ color: 'var(--neg)' }}>en juego</b></> : ''}</p></div>
                    <div className="u"><p className="p m">Edge {fmtNum(a.edge)}</p><p className="d">📡 Radar</p></div>
                  </button>
                ))}
              </div>
            ))}
          {!acceso && rGrupos.length > 3 && <NoAccess que="Solo ves los últimos 3 días de alertas RADAR." />}
        </>
      )}
    </div></div>
  );
}
